import { useEffect, useMemo, useState } from "react";
import { Wallet, Flame, Snowflake, Clock, ArrowDown, ArrowUp, FileWarning, Users as UsersIcon, FileText, Network } from "lucide-react";
import AdminShell, { Panel, StatusPill, MockBadge, Btn } from "@/components/admin/AdminShell";
import KpiCard from "@/components/admin/KpiCard";
import { PrimaryTabs, SecondaryTabs } from "@/components/admin/SubTabs";
import DataTable from "@/components/admin/DataTable";
import AdminPager from "@/components/admin/AdminPager";
import { api } from "@/lib/api";
import { useAdminPager, unwrapAdminList } from "@/lib/useAdminPager";

const TABS = [
  { id: "assets", label: "Assets & Networks", icon: Network },
  { id: "overview", label: "Wallet Overview", icon: Wallet },
  { id: "deposits", label: "Deposits", icon: ArrowDown },
  { id: "withdrawals", label: "Withdrawals", icon: ArrowUp },
  { id: "hotcold", label: "Hot & Cold", icon: Flame },
  { id: "amlfiu", label: "AML & FIU", icon: FileWarning },
  { id: "userwallets", label: "User Wallets", icon: UsersIcon },
  { id: "audit", label: "Audit Logs", icon: FileText },
];

function useLiveUsdPrices() {
  const [prices, setPrices] = useState({ USDT: 1, USDC: 1 });
  useEffect(() => {
    api
      .get("/api/markets/list", { params: { quote: "USDT", category: "all", page_size: 50 } })
      .then(({ data }) => {
        const next = { USDT: 1, USDC: 1 };
        (data?.rows ?? []).forEach((r) => {
          if (r.symbol && Number.isFinite(r.price)) next[r.symbol] = r.price;
        });
        setPrices(next);
      })
      .catch(() => {});
  }, []);
  return prices;
}

export default function WalletManagement() {
  const [tab, setTab] = useState("assets");
  const [assets, setAssets] = useState([]);
  const [kpis, setKpis] = useState({ pending_withdrawals: 0, deposits_today: 0, withdrawals_today: 0 });
  const prices = useLiveUsdPrices();

  useEffect(() => {
    api.get("/api/admin/wallet/assets").then(({ data }) => setAssets(data)).catch(() => {});
    api.get("/api/admin/wallet/kpis").then(({ data }) => setKpis(data)).catch(() => {});
  }, []);

  const total = useMemo(
    () => assets.reduce((s, a) => s + (a.total_balance || 0) * (prices[a.symbol] || 0), 0),
    [assets, prices],
  );
  const hot = useMemo(
    () => assets.reduce((s, a) => s + (a.hot_balance || 0) * (prices[a.symbol] || 0), 0),
    [assets, prices],
  );
  const cold = total - hot;

  return (
    <AdminShell title="Wallet Management">
      <div className="mb-5"><MockBadge phase="B" /></div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        <KpiCard label="Total Assets" value={`$${(total / 1e6).toFixed(2)}M`} icon={Wallet} variant="yellow" />
        <KpiCard label="Hot Wallet" value={`$${(hot / 1e6).toFixed(2)}M`} delta={total ? `${((hot / total) * 100).toFixed(1)}%` : "—"} deltaLabel="of total" icon={Flame} variant="red" />
        <KpiCard label="Cold Wallet" value={`$${(cold / 1e6).toFixed(2)}M`} delta={total ? `${((cold / total) * 100).toFixed(1)}%` : "—"} deltaLabel="of total" icon={Snowflake} variant="blue" />
        <KpiCard label="Pending Withdrawals" value={String(kpis.pending_withdrawals ?? 0)} sub={`${kpis.deposits_today ?? 0} deposits today`} icon={Clock} variant="purple" />
      </div>

      <PrimaryTabs tabs={TABS} value={tab} onChange={setTab} testIdPrefix="wallet-tab" />
      <div className="mt-5">
        {tab === "assets" && <AssetsTab assets={assets} prices={prices} />}
        {tab === "overview" && <OverviewTab assets={assets} prices={prices} />}
        {tab === "deposits" && <DepositsTab />}
        {tab === "withdrawals" && <WithdrawalsTab />}
        {tab === "hotcold" && <HotColdTab assets={assets} />}
        {tab === "amlfiu" && <AmlFiuTab />}
        {tab === "userwallets" && <UserWalletsTab />}
        {tab === "audit" && <AuditTab />}
      </div>
    </AdminShell>
  );
}

const ASSET_SUBTABS = [{ id: "all", label: "All Assets" }, { id: "networks", label: "Network Configuration" }];

function AssetsTab({ assets, prices }) {
  const [sub, setSub] = useState("all");
  return (
    <div>
      <SecondaryTabs tabs={ASSET_SUBTABS} value={sub} onChange={setSub} testIdPrefix="assets-sub" />
      {sub === "all" && (
        <Panel className="mt-4">
          <DataTable
            testid="assets-table"
            columns={[
              { key: "asset", header: "Asset", render: (a) => <div><div className="font-bold">{a.symbol}</div><div className="text-[11px] text-[#8892A8]">{a.name}</div></div> },
              { key: "decimals", header: "Decimals", render: (a) => a.decimals },
              { key: "networks", header: "Networks", render: (a) => <div className="flex flex-wrap gap-1">{a.networks?.map((n) => <span key={n} className="text-[10px] bg-[#1A2138] px-2 py-0.5 rounded text-[#8892A8]">{n}</span>)}</div> },
              { key: "total", header: "Total Balance", align: "right", render: (a) => <div><div className="font-bold tabular-nums">{(a.total_balance || 0).toLocaleString()}</div><div className="text-[10px] text-[#8892A8]">${((a.total_balance || 0) * (prices[a.symbol] || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div></div> },
              { key: "min_dep", header: "Min Deposit", align: "right", render: (a) => a.min_deposit },
              { key: "max_with", header: "Max Withdraw", align: "right", render: (a) => a.max_withdraw?.toLocaleString() },
              { key: "status", header: "Status", render: (a) => <StatusPill value={a.status} /> },
            ]}
            rows={assets}
            rowKey={(a) => a.id}
          />
        </Panel>
      )}
      {sub === "networks" && (
        <Panel className="mt-4">
          <DataTable
            columns={[
              { key: "asset", header: "Asset" },
              { key: "network", header: "Network" },
              { key: "min_conf", header: "Min Confirmations", align: "right" },
              { key: "fee", header: "Network Fee", align: "right" },
              { key: "status", header: "Status", render: (r) => <StatusPill value={r.status} /> },
            ]}
            rows={assets.flatMap((a) => (a.networks || []).map((n) => ({ asset: a.symbol, network: n, min_conf: 3, fee: "0.0001", status: "active" })))}
          />
        </Panel>
      )}
    </div>
  );
}

function OverviewTab({ assets, prices }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      {assets.map((a) => (
        <Panel key={a.id} className="p-5">
          <div className="flex justify-between items-start mb-3">
            <div><div className="font-bold text-[16px]">{a.symbol}</div><div className="text-[11px] text-[#8892A8]">{a.name}</div></div>
            <StatusPill value={a.status} />
          </div>
          <div className="text-[24px] font-bold tabular-nums">{(a.total_balance || 0).toLocaleString()}</div>
          <div className="text-[12px] text-[#8892A8] mt-1">≈ ${((a.total_balance || 0) * (prices[a.symbol] || 0)).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
          <div className="grid grid-cols-2 gap-2 mt-4 text-[11px]">
            <div className="p-2 bg-[#0A0F1F] rounded"><span className="text-[#EF4444]">🔥 Hot</span> <div className="font-bold tabular-nums">{(a.hot_balance || 0).toLocaleString()}</div></div>
            <div className="p-2 bg-[#0A0F1F] rounded"><span className="text-[#3B82F6]">❄ Cold</span> <div className="font-bold tabular-nums">{(a.cold_balance || 0).toLocaleString()}</div></div>
          </div>
        </Panel>
      ))}
    </div>
  );
}

function DepositsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const pager = useAdminPager();
  useEffect(() => {
    setLoading(true);
    api
      .get("/api/admin/wallet/deposits", { params: { skip: pager.skip, limit: pager.limit } })
      .then(({ data }) => {
        const { items, total } = unwrapAdminList(data, "deposits");
        setRows(items);
        pager.setTotal(total);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pager.page]);
  if (loading) return <Panel className="p-8 text-center text-[#8892A8]">Loading deposits…</Panel>;
  return (
    <Panel>
      <DataTable
        columns={[
          { key: "tx_id", header: "Deposit ID", render: (r) => <span className="font-mono text-[11px]">{r.tx_id}</span> },
          { key: "user_email", header: "User", render: (r) => r.user_email || r.user_id },
          { key: "asset", header: "Asset", render: (r) => <span className="font-bold">{r.asset}</span> },
          { key: "network", header: "Network" },
          { key: "amount", header: "Amount", align: "right", render: (r) => `${r.amount} ${r.asset}` },
          { key: "confirmations", header: "Confs", align: "right", render: (r) => `${r.confirmations ?? 0}/${r.required_confirmations ?? "—"}` },
          { key: "status", header: "Status", render: (r) => <StatusPill value={r.status} /> },
          { key: "created_at", header: "When", render: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : "—" },
        ]}
        rows={rows}
        rowKey={(r) => r.tx_id}
        empty="No deposits in ledger yet."
      />
      <AdminPager page={pager.page} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} testId="wallet-deposits-pager" />
    </Panel>
  );
}

function WithdrawalsTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const pager = useAdminPager();
  useEffect(() => {
    setLoading(true);
    api
      .get("/api/admin/wallet/withdrawals", { params: { skip: pager.skip, limit: pager.limit } })
      .then(({ data }) => {
        const { items, total } = unwrapAdminList(data, "withdrawals");
        setRows(items);
        pager.setTotal(total);
      })
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pager.page]);
  if (loading) return <Panel className="p-8 text-center text-[#8892A8]">Loading withdrawals…</Panel>;
  return (
    <Panel>
      <DataTable
        columns={[
          { key: "tx_id", header: "Withdrawal ID", render: (r) => <span className="font-mono text-[11px]">{r.tx_id}</span> },
          { key: "user_id", header: "User", render: (r) => r.user_email || r.user_id },
          { key: "asset", header: "Asset", render: (r) => <span className="font-bold">{r.asset}</span> },
          { key: "network", header: "Network" },
          { key: "amount", header: "Amount", align: "right", render: (r) => `${r.amount} ${r.asset}` },
          { key: "to_address", header: "Destination", render: (r) => <span className="font-mono text-[11px]">{r.to_address ? `${String(r.to_address).slice(0, 14)}…` : "—"}</span> },
          { key: "status", header: "Status", render: (r) => <StatusPill value={r.status} /> },
          { key: "created_at", header: "When", render: (r) => r.created_at ? new Date(r.created_at).toLocaleString() : "—" },
        ]}
        rows={rows}
        rowKey={(r) => r.tx_id}
        empty="No withdrawal requests yet."
      />
      <AdminPager page={pager.page} total={pager.total} pageSize={pager.pageSize} onPage={pager.setPage} testId="wallet-withdrawals-pager" />
    </Panel>
  );
}

function HotColdTab({ assets }) {
  return (
    <Panel>
      <DataTable
        columns={[
          { key: "asset", header: "Asset", render: (a) => <span className="font-bold">{a.symbol}</span> },
          { key: "hot", header: "Hot Balance", align: "right", render: (a) => <span className="text-[#EF4444]">{(a.hot_balance || 0).toLocaleString()}</span> },
          { key: "cold", header: "Cold Balance", align: "right", render: (a) => <span className="text-[#3B82F6]">{(a.cold_balance || 0).toLocaleString()}</span> },
          { key: "ratio", header: "Hot %", align: "right", render: (a) => (a.total_balance ? `${((a.hot_balance / a.total_balance) * 100).toFixed(1)}%` : "—") },
          { key: "actions", header: "Actions", align: "right", render: () => <button type="button" className="text-[#F0B83A] text-[11px] font-bold hover:underline">Rebalance</button> },
        ]}
        rows={assets}
        rowKey={(a) => a.id}
      />
    </Panel>
  );
}

function AmlFiuTab() {
  return <Panel className="p-10 text-center text-[#8892A8] text-[13px]">Wallet-level AML hooks — see the <a href="/admin/aml" className="text-[#F0B83A] hover:underline">FIU/AML module</a> for the full alert queue.</Panel>;
}

function UserWalletsTab() {
  return <Panel className="p-10 text-center text-[#8892A8] text-[13px]">Per-user wallet drilldown — accessed via <a href="/admin/users" className="text-[#F0B83A] hover:underline">User Management → User Detail → Wallets tab</a>.</Panel>;
}

function AuditTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    api
      .get("/api/admin/audit-log", { params: { limit: 30 } })
      .then(({ data }) => setLogs(Array.isArray(data) ? data : data?.logs ?? []))
      .catch(() => setLogs([]));
  }, []);
  return (
    <Panel>
      <DataTable
        columns={[
          { key: "at", header: "When", render: (r) => r.at || r.created_at },
          { key: "actor", header: "Admin", render: (r) => r.actor || r.actor_email },
          { key: "action", header: "Action" },
          { key: "target_id", header: "Target", render: (r) => r.target_id || r.target },
        ]}
        rows={logs}
        rowKey={(r, i) => r.target_id || i}
      />
    </Panel>
  );
}
